import React, { useEffect, useState } from 'react';
import { Bell, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { relativeTime } from '@/lib/format';
import { useNavigate } from 'react-router-dom';

export default function NotificationBell() {
    const { user, profile } = useAuth();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const navigate = useNavigate();
    const unread = items.filter((n) => !n.read).length;

    const load = async () => {
        if (!user) return;
        const { data } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(15);
        setItems(data || []);
    };

    useEffect(() => { load(); }, [user?.id]);

    useEffect(() => {
        if (!user) return;
        const close = (e) => {
            // Don't close when clicking the bell itself or items inside the dropdown
            if (e.target?.closest?.('[data-notif-root]')) return;
            setOpen(false);
        };
        window.addEventListener('click', close);
        return () => window.removeEventListener('click', close);
    }, [user?.id]);

    useEffect(() => {
        if (!user) return;
        const ch = supabase
            .channel(`notif-${user.id}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` }, (payload) => {
                setItems((prev) => [payload.new, ...prev].slice(0, 15));
            })
            .subscribe();
        return () => { supabase.removeChannel(ch); };
    }, [user?.id]);

    const markRead = async (id) => {
        await supabase.from('notifications').update({ read: true }).eq('id', id);
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    };
    const markAllRead = async () => {
        if (!user) return;
        await supabase.from('notifications').update({ read: true }).eq('user_id', user.id).eq('read', false);
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    };

    const onClick = (n) => {
        markRead(n.id);
        setOpen(false);
        if (n.related_module === 'pipeline_contacts' && n.related_id) navigate(`/pipeline/${n.related_id}`);
    };

    return (
        <div className="relative" data-notif-root onClick={(e) => e.stopPropagation()}>
            <button
                onClick={() => setOpen((s) => !s)}
                className="relative h-9 w-9 rounded-md hover:bg-gray-100 inline-flex items-center justify-center aks-focus-ring"
                aria-label="Notifications"
                data-testid="notif-bell"
            >
                <Bell className="h-5 w-5 text-aks-text" />
                {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-aks-danger text-white text-[10px] font-bold flex items-center justify-center">
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 top-11 w-[360px] max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="notif-dropdown">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                        <div className="text-[13px] font-semibold text-aks-text">Notifications</div>
                        {unread > 0 && (
                            <button onClick={markAllRead} className="text-[11px] text-aks-navy hover:text-aks-gold flex items-center gap-1">
                                <Check className="h-3 w-3" /> Mark all read
                            </button>
                        )}
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="py-10 text-center text-[12px] text-gray-500">No notifications yet</div>
                        ) : items.map((n) => (
                            <button
                                key={n.id}
                                onClick={() => onClick(n)}
                                className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 ${!n.read ? 'bg-[rgba(201,168,76,0.06)]' : ''}`}
                            >
                                <div className="flex items-start gap-2">
                                    {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-aks-gold flex-shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-medium text-aks-text truncate">{n.title}</div>
                                        <div className="text-[12px] text-gray-600 truncate">{n.message}</div>
                                        <div className="text-[10px] text-gray-400 mt-0.5">{relativeTime(n.created_at)}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
